/**
 * In-app ward submit retirement for four compliance categories.
 * Report / Dashboard / team views stay; open-to-edit and Firestore submit are closed.
 */

export const WARD_SUBMIT_RETIRED_CATEGORY_IDS = [
  'nurse_notes',
  'needleandsharp',
  'med_error',
  'siren_bundle',
] as const;

export type WardSubmitRetiredCategoryId = (typeof WARD_SUBMIT_RETIRED_CATEGORY_IDS)[number];

/** Display titles only — never document ids. Supervision `นิเทศทางการพยาบาล` is not retired. */
export const WARD_SUBMIT_RETIRED_TITLES = [
  'บันทึกทางการพยาบาล',
  'เข็มตำ/ของมีคมบาด',
  'นิเทศระบบยา',
  'SIREN bundle',
] as const;

const RETIRED_ID_SET = new Set<string>(WARD_SUBMIT_RETIRED_CATEGORY_IDS);
const RETIRED_TITLE_SET = new Set<string>(WARD_SUBMIT_RETIRED_TITLES);

const ID_TO_DISPLAY_TITLE: Record<WardSubmitRetiredCategoryId, string> = {
  nurse_notes: 'บันทึกทางการพยาบาล',
  needleandsharp: 'เข็มตำ/ของมีคมบาด',
  med_error: 'นิเทศระบบยา',
  siren_bundle: 'SIREN bundle',
};

export const WARD_SUBMIT_CLOSED_NEXT_STEP = 'กรุณาไปที่หน้าแรก หมวดแบบฟอร์ม หรือ งานคุณภาพ';

export type WardSubmitRetiredLookup = {
  categoryDocId?: string | null;
  title?: string | null;
};

export type WardSubmitClosedMessage = {
  primary: string;
  nextStep: string;
};

function trimOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function isWardSubmitRetired(input: WardSubmitRetiredLookup | string | null | undefined): boolean {
  if (input == null) return false;
  if (typeof input === 'string') {
    const token = input.trim();
    if (!token) return false;
    return RETIRED_ID_SET.has(token) || RETIRED_TITLE_SET.has(token);
  }
  const id = trimOrEmpty(input.categoryDocId);
  const title = trimOrEmpty(input.title);
  if (id && RETIRED_ID_SET.has(id)) return true;
  if (title && RETIRED_TITLE_SET.has(title)) return true;
  return false;
}

/** Display title for closed copy. Never returns a raw category document id. */
export function resolveWardSubmitDisplayTitle(input: WardSubmitRetiredLookup | string | null | undefined): string | null {
  if (input == null) return null;
  if (typeof input === 'string') {
    const token = input.trim();
    if (!token) return null;
    if (RETIRED_TITLE_SET.has(token)) return token;
    if (RETIRED_ID_SET.has(token)) return ID_TO_DISPLAY_TITLE[token as WardSubmitRetiredCategoryId];
    return null;
  }
  const title = trimOrEmpty(input.title);
  if (title && RETIRED_TITLE_SET.has(title)) return title;
  const id = trimOrEmpty(input.categoryDocId);
  if (id && RETIRED_ID_SET.has(id)) return ID_TO_DISPLAY_TITLE[id as WardSubmitRetiredCategoryId];
  if (title) return title;
  return null;
}

export function wardSubmitOpenClosedMessage(displayTitle: string): WardSubmitClosedMessage {
  const name = displayTitle.trim() || 'ไม่ทราบชื่อหมวด';
  return {
    primary: `แบบฟอร์ม${name} ปิดรับส่งในแอปแล้ว ไม่ได้เปิดหน้ากรอก`,
    nextStep: WARD_SUBMIT_CLOSED_NEXT_STEP,
  };
}

export function wardSubmitSubmitClosedMessage(displayTitle: string): WardSubmitClosedMessage {
  const name = displayTitle.trim() || 'ไม่ทราบชื่อหมวด';
  return {
    primary: `แบบฟอร์ม${name} ปิดรับส่งในแอปแล้ว ข้อมูลนี้ยังไม่ได้บันทึก`,
    nextStep: WARD_SUBMIT_CLOSED_NEXT_STEP,
  };
}

export function formatWardSubmitClosedMessage(message: WardSubmitClosedMessage): string {
  return `${message.primary}\n${message.nextStep}`;
}
